import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, API } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Users, AlertTriangle, FileText, LifeBuoy, Activity, ArrowLeft, LogOut, ShieldCheck, ChevronUp, ChevronDown, Send, CheckCircle2 } from 'lucide-react';

const TABS = ['customers', 'alerts', 'tickets', 'invoices', 'logs', 'package_requests'];
const tabTestId = {
  customers: APP.adminTabCustomers, alerts: APP.adminTabAlerts, tickets: APP.adminTabTickets,
  invoices: APP.adminTabInvoices, logs: APP.adminTabLogs, package_requests: APP.adminTabPackageReqs,
};

const sevPill = { low: 'pill-low', medium: 'pill-medium', high: 'pill-high', critical: 'pill-critical' };

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('customers');
  const [customers, setCustomers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [logs, setLogs] = useState([]);
  const [pkgReqs, setPkgReqs] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);

  const loadStats = async () => setStats((await api.get('/admin/stats')).data);
  const loadCustomers = async () => setCustomers((await api.get('/admin/customers')).data);
  const loadAlerts = async () => setAlerts((await api.get('/admin/alerts')).data);
  const loadTickets = async () => setTickets((await api.get('/admin/tickets')).data);
  const loadLogs = async () => setLogs((await api.get('/admin/activity-logs')).data);
  const loadPkgReqs = async () => setPkgReqs((await api.get('/admin/package-requests')).data);

  useEffect(() => { loadStats(); loadCustomers(); }, []);
  useEffect(() => {
    if (tab === 'alerts') loadAlerts();
    if (tab === 'tickets') loadTickets();
    if (tab === 'logs') loadLogs();
    if (tab === 'package_requests' || tab === 'invoices') loadPkgReqs();
  }, [tab]);

  return (
    <div data-testid={APP.adminDashboard} className="min-h-screen flex flex-col bg-[color:var(--bg-base)]">
      {/* Top bar */}
      <header className="border-b border-[color:var(--border)] bg-[color:var(--bg-base)]/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 md:px-12 h-14 md:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="logo-mark shrink-0">S</span>
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase truncate">Supreme · Admin</div>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--brand)] truncate">Security Ops</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Link to="/app" className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1">
              <ArrowLeft size={12} /> <span className="hidden sm:inline">Customer view</span><span className="sm:hidden">View</span>
            </Link>
            <button data-testid={APP.logoutBtn} onClick={logout} className="btn-secondary !py-2 !px-3 !text-[10px]">
              <LogOut size={12} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 md:px-12 py-6 md:py-10 w-full flex-1">
        <div className="mb-6 md:mb-10">
          <div className="label-tag mb-2">// Admin Dashboard</div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight">Command center</h1>
          <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl text-sm md:text-base">
            Logged in as <span className="text-[color:var(--brand)] break-all">{user?.email}</span>. Manage customers, scans, packages, invoices, and incidents.
          </p>
        </div>

        {/* Stat row */}
        {stats && (
          <section className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[color:var(--border)] mb-6 md:mb-10">
            <StatCard label="Customers" value={stats.total_customers} icon={<Users size={14} />} />
            <StatCard label="Scans" value={stats.total_scans} icon={<Activity size={14} />} />
            <StatCard label="Open Alerts" value={stats.open_alerts} icon={<AlertTriangle size={14} />} accent={stats.open_alerts ? 'text-[color:var(--status-high)]' : ''} />
            <StatCard label="Open Tickets" value={stats.open_tickets} icon={<LifeBuoy size={14} />} />
            <StatCard label="Pkg Requests" value={stats.package_requests_pending} icon={<FileText size={14} />} />
          </section>
        )}

        {/* Tabs — horizontal scroll on mobile */}
        <div className="flex gap-2 mb-6 border-b border-[color:var(--border)] pb-3 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 scrollbar-thin">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={tabTestId[t]}
              onClick={() => setTab(t)}
              className={`pill ${tab === t ? 'pill-brand' : 'pill-muted'} shrink-0`}
            >
              {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        {tab === 'customers' && (
          selectedCust ? (
            <CustomerDetail userId={selectedCust} onBack={() => { setSelectedCust(null); loadCustomers(); loadStats(); }} />
          ) : (
            <CustomersTable customers={customers} onSelect={setSelectedCust} />
          )
        )}
        {tab === 'alerts' && <AlertsTable alerts={alerts} />}
        {tab === 'tickets' && <TicketsTable tickets={tickets} reload={loadTickets} />}
        {tab === 'invoices' && <InvoicesView />}
        {tab === 'logs' && <LogsTable logs={logs} />}
        {tab === 'package_requests' && <PackageRequests reqs={pkgReqs} reload={loadPkgReqs} />}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, icon, accent }) => (
  <div className="bg-[color:var(--bg-base)] p-5">
    <div className="flex items-center justify-between text-[color:var(--text-muted)] mb-3">
      <span className="label-tag">{label}</span>{icon}
    </div>
    <div className={`text-3xl font-light ${accent || ''}`}>{value}</div>
  </div>
);

const CustomersTable = ({ customers, onSelect }) => (
  <div className="panel overflow-x-auto">
    <table className="dtable min-w-[960px]">
      <thead><tr><th>Customer</th><th>Email</th><th>Package</th><th>Payment</th><th>Credits</th><th>Scans</th><th>Open Alerts</th><th>Joined</th></tr></thead>
      <tbody>
        {customers.length === 0 ? (
          <tr><td colSpan={8} className="text-[color:var(--text-secondary)]">No customers yet.</td></tr>
        ) : customers.map((c) => (
          <tr key={c.user_id} data-testid={APP.adminCustomerRow} className="cursor-pointer" onClick={() => onSelect(c.user_id)}>
            <td>{c.name}</td>
            <td className="text-[color:var(--brand)]">{c.email}</td>
            <td><span className="pill pill-muted">{c.package || 'none'}</span></td>
            <td><span className={`pill ${c.payment_status === 'paid' ? 'pill-low' : 'pill-medium'}`}>{c.payment_status || 'unpaid'}</span></td>
            <td className={(c.credits || 0) > 0 ? 'text-[color:var(--brand)]' : 'text-[color:var(--status-medium)]'}>{(c.credits || 0).toLocaleString()}</td>
            <td>{c.scan_count}</td>
            <td className={c.open_alerts ? 'text-[color:var(--status-high)]' : ''}>{c.open_alerts}</td>
            <td className="text-[color:var(--text-secondary)]">{new Date(c.created_at).toLocaleDateString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CustomerDetail = ({ userId, onBack }) => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState('');
  const [pkg, setPkg] = useState('none');
  const [invPkg, setInvPkg] = useState('scan');
  const [invAmt, setInvAmt] = useState('999');
  const [creditAmt, setCreditAmt] = useState('1000');
  const [genForensic, setGenForensic] = useState(false);
  const [forensicReports, setForensicReports] = useState([]);
  const [editingReport, setEditingReport] = useState(null);
  const [confirmingReport, setConfirmingReport] = useState(null);
  const [augmentPrompt, setAugmentPrompt] = useState('');
  const [augmentTitle, setAugmentTitle] = useState('');
  const [augmenting, setAugmenting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [upgradeRequests, setUpgradeRequests] = useState([]);

  const fmtUSD = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const load = async () => {
    const { data: d } = await api.get(`/admin/customers/${userId}`);
    setData(d); setPkg(d.customer.package || 'none');
  };
  const loadForensic = async () => {
    try {
      const { data: list } = await api.get(`/admin/customers/${userId}/forensic-reports`);
      setForensicReports(list);
    } catch (_e) { /* noop */ }
  };
  const loadUpgradeRequests = async () => {
    try {
      const { data: list } = await api.get(`/admin/upgrade-requests`, { params: { user_id: userId } });
      setUpgradeRequests(list);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); loadForensic(); loadUpgradeRequests(); }, [userId]);

  if (!data) return <div className="font-mono text-xs text-[color:var(--text-secondary)] blink">Loading customer…</div>;

  const addCredits = async () => {
    const amt = parseInt(creditAmt, 10);
    if (!amt || Number.isNaN(amt)) { toast.error('Enter a non-zero integer'); return; }
    try {
      const { data: res } = await api.post(`/admin/customers/${userId}/credits`, { amount: amt });
      toast.success(`Balance: ${res.credits.toLocaleString()} (${amt > 0 ? '+' : ''}${amt})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update credits');
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/customers/${userId}/notes`, { body: note });
    setNote(''); toast.success('Note saved'); load();
  };
  const changePkg = async (v) => { await api.post(`/admin/customers/${userId}/package`, { package: v }); setPkg(v); toast.success(`Package set to ${v}`); load(); };
  const togglePayment = async () => {
    const next = data.customer.payment_status === 'paid' ? 'unpaid' : 'paid';
    await api.post(`/admin/customers/${userId}/payment`, { status: next });
    toast.success(`Customer marked as ${next.toUpperCase()}`);
    load();
  };
  const addInvoice = async () => {
    await api.post(`/admin/customers/${userId}/invoices`, { package: invPkg, amount: parseFloat(invAmt) || 0 });
    toast.success('Invoice issued'); load();
  };
  const setRisk = async (level) => {
    // Quick action: assign risk by creating an alert
    toast.info(`Risk acknowledged as ${level.toUpperCase()}`);
  };

  return (
    <div className="space-y-8">
      <button onClick={onBack} className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Back to customers
      </button>
      <div className="panel p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-tag mb-2">// Customer</div>
            <div className="text-3xl font-light">{data.customer.name}</div>
            <div className="font-mono text-xs text-[color:var(--brand)]">{data.customer.email}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-2">
              Payment: <span className={data.customer.payment_status === 'paid' ? 'text-[color:var(--status-low)]' : 'text-[color:var(--status-medium)]'}>
                {data.customer.payment_status || 'unpaid'}
              </span>
              {data.customer.payment_marked_at && (
                <> · marked {new Date(data.customer.payment_marked_at).toLocaleDateString()} by {data.customer.payment_marked_by}</>
              )}
              <span className="mx-2">·</span>
              Credits:&nbsp;
              <span className={(data.customer.credits || 0) > 0 ? 'text-[color:var(--brand)]' : 'text-[color:var(--status-medium)]'}>
                {(data.customer.credits || 0).toLocaleString()}
              </span> / {(100000).toLocaleString()}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-payment-toggle"
              onClick={togglePayment}
              className={data.customer.payment_status === 'paid' ? 'btn-destructive' : 'btn-primary'}
              style={{ padding: '10px 18px', fontSize: 11 }}
            >
              {data.customer.payment_status === 'paid' ? 'Mark Unpaid' : 'Mark as Paid'}
            </button>
            <div className="flex items-center gap-2">
              <span className="label-tag">Package:</span>
              <select data-testid={APP.adminPackageSelect} value={pkg} onChange={(e) => changePkg(e.target.value)} className="input-base !w-auto">
                <option value="none">none</option>
                <option value="scan">Scan — $999</option>
                <option value="plus">Plus — $1499</option>
                <option value="web_pi">Web PI — $2500</option>
              </select>
            </div>
            <div className="flex gap-1">
              {['low', 'medium', 'high', 'critical'].map((l) => (
                <button key={l} onClick={() => setRisk(l)} className={`pill ${sevPill[l]} uppercase`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[color:var(--border)]">
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-4">// Scans · {data.scans.length}</div>
          {data.scans.length === 0 ? <Empty>No scans.</Empty> : (
            <ul className="space-y-2">
              {data.scans.slice(0, 8).map((s) => (
                <li key={s.scan_id} className="flex justify-between panel-soft p-3 items-center">
                  <div>
                    <div className="text-sm">{s.network_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{new Date(s.started_at).toLocaleString()}</div>
                  </div>
                  <span className={`pill ${sevPill[s.risk_level]}`}>{s.risk_level}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-4">// Alerts · {data.alerts.length}</div>
          {data.alerts.length === 0 ? <Empty>No alerts.</Empty> : (
            <ul className="space-y-2">
              {data.alerts.slice(0, 8).map((a) => (
                <li key={a.alert_id} className="panel-soft p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">{a.title}</span>
                    <span className={`pill ${sevPill[a.severity]}`}>{a.severity}</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{new Date(a.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="panel p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <div className="label-tag mb-1">// Credits Wallet</div>
            <h3 className="text-xl font-light">Balance: <span className={(data.customer.credits || 0) > 0 ? 'text-[color:var(--brand)] tabular-nums' : 'text-[color:var(--status-medium)] tabular-nums'}>{(data.customer.credits || 0).toLocaleString()}</span> <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">/ {(100000).toLocaleString()} max</span></h3>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">Each scan costs 100 credits. {(data.customer.credits || 0) > 0 ? `Approx ${Math.floor((data.customer.credits || 0) / 100)} scans available.` : 'Out of credits — customer is locked until topped up or marked paid.'}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            data-testid="admin-credits-amount"
            type="number"
            value={creditAmt}
            onChange={(e) => setCreditAmt(e.target.value)}
            placeholder="e.g. 1000 (use negative to deduct)"
            className="input-base flex-1"
            max={100000}
            min={-100000}
          />
          <div className="flex gap-2">
            {[500, 1000, 5000, 10000].map((q) => (
              <button key={q} type="button" onClick={() => setCreditAmt(String(q))} className="pill pill-muted hover:!border-[color:var(--brand)] hover:!text-[color:var(--brand)]" data-testid={`admin-credits-preset-${q}`}>
                +{q.toLocaleString()}
              </button>
            ))}
          </div>
          <button data-testid="admin-credits-add" onClick={addCredits} className="btn-primary !py-2 !text-[11px] shrink-0">
            Apply
          </button>
        </div>
        {data.customer.credits_last_updated_at && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-3">
            Last updated {new Date(data.customer.credits_last_updated_at).toLocaleString()} {data.customer.credits_last_updated_by ? `· by ${data.customer.credits_last_updated_by}` : ''}
          </div>
        )}
      </section>

      <section className="panel p-5 sm:p-6" data-testid="admin-forensic-panel">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <div className="label-tag mb-1">// Web PI Forensic Report</div>
            <h3 className="text-xl font-light">Case <span className="font-mono text-[color:var(--brand)]">{data.customer.case_number || '#PENDING'}</span></h3>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">AI-assisted analyst report compiled from this customer&apos;s login feed, scans &amp; devices. Editable below before Mike sees it.</p>
          </div>
          <button
            onClick={async () => {
              setGenForensic(true);
              try {
                await api.post(`/admin/customers/${userId}/forensic-report`, { admin_notes: '' });
                toast.success('Forensic report generated (Claude Sonnet 4.6)');
                loadForensic();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Generation failed');
              } finally { setGenForensic(false); }
            }}
            disabled={genForensic}
            data-testid="admin-forensic-generate"
            className="btn-primary !py-2 !text-[11px] shrink-0"
          >
            {genForensic ? 'Generating…' : 'Generate Report'}
          </button>
        </div>
        {forensicReports.length === 0 ? (
          <div className="font-mono text-xs text-[color:var(--text-secondary)]">No reports yet.</div>
        ) : (
          <ul className="space-y-2" data-testid="admin-forensic-list">
            {forensicReports.map((r) => (
              <li key={r.report_id} className="panel-soft p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="text-sm">{r.investigation_type} <span className="font-mono text-[10px] text-[color:var(--text-muted)] ml-2">{new Date(r.created_at).toLocaleString()}</span></div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    <span>{r.classification || 'Preliminary Findings'}</span>
                    <span>· {r.final_determination || 'Pending Review'}</span>
                    {r.customer_requested_confirmation && !r.confirmed_at && (
                      <span className="text-[color:var(--status-medium)]" data-testid="admin-forensic-pending-confirm">⚠ Customer requested confirmation</span>
                    )}
                    {r.confirmed_at && <span className="text-[color:var(--status-low)]">✓ Confirmed</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setEditingReport(r)} className="btn-secondary !py-1 !px-3 !text-[10px]" data-testid="admin-forensic-edit">Edit</button>
                  {!r.confirmed_at && (
                    <button
                      onClick={() => setConfirmingReport(r)}
                      className={`!py-1 !px-3 !text-[10px] ${r.customer_requested_confirmation ? 'btn-primary' : 'btn-secondary'}`}
                      data-testid="admin-forensic-confirm-open"
                    >
                      Approve Confirmed
                    </button>
                  )}
                  <button onClick={() => { setEditingReport(r); setAugmentTitle(''); setAugmentPrompt(''); }} className="btn-secondary !py-1 !px-3 !text-[10px]" data-testid="admin-forensic-augment-open">AI Augment</button>
                  <a href={`${API}/forensic-reports/${r.report_id}/pdf`} target="_blank" rel="noreferrer" className="btn-secondary !py-1 !px-3 !text-[10px]">PDF</a>
                </div>
              </li>
            ))}
          </ul>
        )}
        {editingReport && (
          <div className="mt-5 panel-soft p-4 space-y-3" data-testid="admin-forensic-editor">
            <div className="label-tag">// Editing {editingReport.report_id}</div>
            <div>
              <div className="label-tag mb-1">Admin Notes</div>
              <textarea rows={3} className="input-base" value={editingReport.admin_notes || ''} onChange={(e) => setEditingReport({ ...editingReport, admin_notes: e.target.value })} />
            </div>
            <div>
              <div className="label-tag mb-1">Confirmed Findings</div>
              <textarea rows={3} className="input-base" value={editingReport.confirmed_findings || ''} onChange={(e) => setEditingReport({ ...editingReport, confirmed_findings: e.target.value })} />
            </div>
            <div>
              <div className="label-tag mb-1">Observed Indicators (AI draft — edit before customer review)</div>
              <textarea rows={5} className="input-base" value={editingReport.ai_observed_indicators || ''} onChange={(e) => setEditingReport({ ...editingReport, ai_observed_indicators: e.target.value })} />
            </div>
            <div>
              <div className="label-tag mb-1">Recommendations (AI draft)</div>
              <textarea rows={5} className="input-base" value={editingReport.ai_recommendations || ''} onChange={(e) => setEditingReport({ ...editingReport, ai_recommendations: e.target.value })} />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    await api.patch(`/admin/forensic-reports/${editingReport.report_id}`, {
                      admin_notes: editingReport.admin_notes,
                      confirmed_findings: editingReport.confirmed_findings,
                      ai_observed_indicators: editingReport.ai_observed_indicators,
                      ai_recommendations: editingReport.ai_recommendations,
                      status: 'final',
                    });
                    toast.success('Report saved & marked final');
                    setEditingReport(null); loadForensic();
                  } catch (e) { toast.error('Save failed'); }
                }}
                className="btn-primary !py-2 !text-[11px]"
                data-testid="admin-forensic-save"
              >Save &amp; Mark Final</button>
              <button onClick={() => setEditingReport(null)} className="btn-secondary !py-2 !text-[11px]">Cancel</button>
            </div>

            {/* AI Augment panel */}
            <div className="mt-5 pt-5 border-t border-[color:var(--border)] space-y-3" data-testid="admin-forensic-augment-panel">
              <div className="label-tag text-[color:var(--brand)]">// AI Augment — Add Extra Detail to Report</div>
              <p className="text-xs text-[color:var(--text-secondary)]">Use Claude Sonnet 4.6 to generate an additional paragraph appended to this report&apos;s PDF. Useful for incident communication guidance, insurance carrier statements, root-cause hypotheses, etc.</p>
              <div>
                <div className="label-tag mb-1">Section Title</div>
                <input
                  type="text"
                  className="input-base"
                  placeholder="e.g. Initial Access Vector & Communication Guidance"
                  value={augmentTitle}
                  onChange={(e) => setAugmentTitle(e.target.value)}
                  data-testid="admin-forensic-augment-title"
                />
              </div>
              <div>
                <div className="label-tag mb-1">Prompt to AI</div>
                <textarea
                  rows={3}
                  className="input-base"
                  placeholder="Describe what you want the AI to add (e.g. write a paragraph explaining how the attacker likely obtained credentials and what to tell customers and insurance)…"
                  value={augmentPrompt}
                  onChange={(e) => setAugmentPrompt(e.target.value)}
                  data-testid="admin-forensic-augment-prompt"
                />
              </div>
              <button
                onClick={async () => {
                  if (!augmentPrompt.trim()) { toast.error('Prompt is required'); return; }
                  setAugmenting(true);
                  try {
                    const { data: updated } = await api.post(
                      `/admin/forensic-reports/${editingReport.report_id}/ai-augment`,
                      { prompt: augmentPrompt, section_title: augmentTitle || 'Additional Analyst Detail' },
                    );
                    toast.success('AI augmentation added to report');
                    setEditingReport(updated);
                    setAugmentPrompt(''); setAugmentTitle('');
                    loadForensic();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'AI augment failed');
                  } finally { setAugmenting(false); }
                }}
                disabled={augmenting || !augmentPrompt.trim()}
                className="btn-primary !py-2 !text-[11px] disabled:opacity-40"
                data-testid="admin-forensic-augment-submit"
              >
                {augmenting ? 'Generating with Claude…' : 'Generate & Append'}
              </button>
              {(editingReport.ai_augmentations || []).length > 0 && (
                <div className="space-y-2 mt-2">
                  <div className="label-tag">// Existing Augmentations ({editingReport.ai_augmentations.length})</div>
                  {editingReport.ai_augmentations.map((aug, i) => (
                    <div key={i} className="panel-soft p-3">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{aug.section_title}</div>
                      <div className="text-xs mt-1 whitespace-pre-wrap leading-relaxed">{aug.body}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">{aug.created_by} · {aug.created_at ? new Date(aug.created_at).toLocaleString() : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirm Findings modal */}
        {confirmingReport && (
          <div className="mt-5 panel-soft p-4 space-y-3 border-[color:var(--status-critical)]" data-testid="admin-forensic-confirm-panel">
            <div className="label-tag text-[color:var(--status-critical)]">// Approve Confirmed Findings — {confirmingReport.report_id}</div>
            <p className="text-xs text-[color:var(--text-secondary)]">
              This will flip the report to <b>Confirmed Findings</b> + <b>Unauthorized Access Confirmed</b>, publish it to the customer, and unlock the final PDF.
            </p>
            <div>
              <div className="label-tag mb-1">Confirmed Findings Narrative (optional — default will be used if empty)</div>
              <textarea
                rows={5}
                className="input-base"
                placeholder="Leave empty to use the default analyst narrative…"
                value={confirmingReport.confirmed_findings || ''}
                onChange={(e) => setConfirmingReport({ ...confirmingReport, confirmed_findings: e.target.value })}
                data-testid="admin-forensic-confirm-narrative"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  setConfirming(true);
                  try {
                    await api.post(`/admin/forensic-reports/${confirmingReport.report_id}/confirm-findings`, {
                      confirmed_findings: confirmingReport.confirmed_findings || '',
                    });
                    toast.success('Confirmed Findings published to customer');
                    setConfirmingReport(null);
                    loadForensic();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Confirm failed');
                  } finally { setConfirming(false); }
                }}
                disabled={confirming}
                className="btn-primary !py-2 !text-[11px] disabled:opacity-40"
                data-testid="admin-forensic-confirm-submit"
              >
                {confirming ? 'Publishing…' : 'Publish Confirmed Findings'}
              </button>
              <button onClick={() => setConfirmingReport(null)} className="btn-secondary !py-2 !text-[11px]">Cancel</button>
            </div>
          </div>
        )}
      </section>

      {/* ---------------- Web PI Package Upgrade Requests ---------------- */}
      <section className="bg-[color:var(--bg-base)] p-6" data-testid="admin-upgrade-requests-section">
        <div className="flex items-center justify-between mb-4">
          <div className="label-tag">// Web PI Package Upgrade Requests</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            {upgradeRequests.length} total · {upgradeRequests.filter((r) => r.status === 'pending_admin').length} pending
          </div>
        </div>
        {upgradeRequests.length === 0 ? (
          <Empty>No upgrade requests yet.</Empty>
        ) : (
          <ul className="space-y-2" data-testid="admin-upgrade-requests-list">
            {upgradeRequests.map((r) => (
              <li key={r.request_id} className={`panel-soft p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between ${r.status === 'pending_admin' ? 'border-l-2 border-l-[color:var(--status-medium)]' : ''}`}>
                <div className="min-w-0">
                  <div className="text-sm flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{r.case_number || r.report_id}</span>
                    <span>{r.package_label || 'Web PI Package'}</span>
                    {r.status === 'pending_admin' && <span className="pill pill-medium" data-testid="admin-upgrade-pending-pill">Pending Admin</span>}
                    {r.status === 'approved' && <span className="pill pill-low" data-testid="admin-upgrade-approved-pill">Approved</span>}
                    {r.status === 'paid_in_full' && <span className="pill pill-brand" data-testid="admin-upgrade-paid-in-full-pill">Paid In Full</span>}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Outstanding: <span className="text-[color:var(--status-critical)] normal-case font-sans">{fmtUSD(r.outstanding_balance_cents)}</span></span>
                    <span>Payment to unlock: <span className="text-[color:var(--text-primary)] normal-case font-sans">{fmtUSD(r.payment_required_cents)}</span></span>
                    {r.status === 'approved' && (
                      <span>Remaining after approval: <span className="text-[color:var(--text-primary)] normal-case font-sans">{fmtUSD(r.remaining_balance_cents)}</span></span>
                    )}
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  {r.approved_by && (
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                      Approved by {r.approved_by} · {r.approved_at ? new Date(r.approved_at).toLocaleString() : ''}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status === 'pending_admin' && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/admin/upgrade-requests/${r.request_id}/approve`);
                          toast.success('Upgrade approved. Customer can now download Confirmed Findings PDF.');
                          loadUpgradeRequests(); load();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
                      }}
                      className="btn-primary !py-1 !px-3 !text-[10px]"
                      data-testid={`admin-upgrade-approve-${r.request_id}`}
                    >
                      Approve &amp; Unlock
                    </button>
                  )}
                  {r.status === 'approved' && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/admin/upgrade-requests/${r.request_id}/mark-paid-in-full`);
                          toast.success('Account marked Paid In Full. Customer can now download the State Farm check image.');
                          loadUpgradeRequests(); load();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Mark Paid failed'); }
                      }}
                      className="btn-primary !py-1 !px-3 !text-[10px]"
                      data-testid={`admin-upgrade-mark-paid-${r.request_id}`}
                    >
                      Mark Paid In Full
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[color:var(--border)]">
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-4">// Technician Notes</div>
          <div className="flex flex-col gap-3 mb-5">
            <textarea data-testid={APP.adminNoteInput} value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="input-base" placeholder="Add an internal note…" />
            <button data-testid={APP.adminNoteSubmit} onClick={addNote} className="btn-primary w-fit !py-2 !text-[11px]">
              <ShieldCheck size={12} /> Save note
            </button>
          </div>
          <ul className="space-y-2">
            {data.notes.length === 0 ? <Empty>No notes yet.</Empty> : data.notes.map((n) => (
              <li key={n.note_id} className="panel-soft p-3">
                <div className="text-sm">{n.body}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                  {n.author_name} · {new Date(n.created_at).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-[color:var(--bg-base)] p-6">
          <div className="label-tag mb-4">// Invoices</div>
          <div className="grid grid-cols-3 gap-2 mb-5">
            <select value={invPkg} onChange={(e) => setInvPkg(e.target.value)} className="input-base">
              <option value="scan">Scan</option>
              <option value="plus">Plus</option>
              <option value="web_pi">Web PI</option>
            </select>
            <input value={invAmt} onChange={(e) => setInvAmt(e.target.value)} className="input-base" />
            <button data-testid={APP.adminInvoiceCreate} onClick={addInvoice} className="btn-primary !py-2 !text-[11px]">Issue</button>
          </div>
          <ul className="space-y-2">
            {data.invoices.length === 0 ? <Empty>No invoices.</Empty> : data.invoices.map((i) => (
              <li key={i.invoice_id} className="flex justify-between panel-soft p-3 items-center">
                <div>
                  <div className="text-sm">${i.amount} · {i.package}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{new Date(i.issued_at).toLocaleDateString()}</div>
                </div>
                <span className="pill pill-muted">{i.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

const AlertsTable = ({ alerts }) => (
  <div className="panel overflow-x-auto">
    <table className="dtable min-w-[900px]">
      <thead><tr><th>Title</th><th>Type</th><th>Severity</th><th>Customer</th><th>Status</th><th>Date</th></tr></thead>
      <tbody>
        {alerts.length === 0 ? <tr><td colSpan={6} className="text-[color:var(--text-secondary)]">No alerts.</td></tr> :
        alerts.map((a) => (
          <tr key={a.alert_id}>
            <td>{a.title}</td>
            <td className="text-[color:var(--text-secondary)]">{a.alert_type.replace(/_/g, ' ')}</td>
            <td><span className={`pill ${sevPill[a.severity]}`}>{a.severity}</span></td>
            <td className="font-mono">{a.user_id.slice(-8)}</td>
            <td>{a.resolved ? <span className="pill pill-low">resolved</span> : <span className="pill pill-medium">open</span>}</td>
            <td className="text-[color:var(--text-secondary)]">{new Date(a.created_at).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TicketsTable = ({ tickets, reload }) => {
  const [openId, setOpenId] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const resolve = async (id) => { await api.patch(`/admin/tickets/${id}`, { status: 'resolved' }); toast.success('Ticket resolved'); reload(); };

  const sendReply = async (id) => {
    if (!replyBody.trim()) { toast.error('Type a reply first'); return; }
    setSending(true);
    try {
      await api.post(`/admin/tickets/${id}/reply`, { body: replyBody });
      setReplyBody('');
      toast.success('Reply sent — customer notified');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  if (tickets.length === 0) {
    return <div className="panel p-6 text-[color:var(--text-secondary)] text-sm">No tickets yet.</div>;
  }

  return (
    <div className="space-y-3" data-testid="admin-tickets-list">
      {tickets.map((t) => {
        const isOpen = openId === t.ticket_id;
        const responses = t.responses || [];
        return (
          <div key={t.ticket_id} className="panel" data-testid="admin-ticket-card">
            <button
              type="button"
              onClick={() => { setOpenId(isOpen ? null : t.ticket_id); setReplyBody(''); }}
              className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-[color:var(--bg-surface)] transition-colors"
              data-testid="admin-ticket-toggle"
            >
              <div className="min-w-0">
                <div className="text-sm sm:text-base font-medium truncate">{t.subject}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                  <span className="text-[color:var(--brand)]">{t.user_email}</span>
                  &nbsp;·&nbsp;{new Date(t.created_at).toLocaleString()}
                  {responses.length > 0 && <> · {responses.length} repl{responses.length === 1 ? 'y' : 'ies'}</>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`pill ${t.status === 'resolved' ? 'pill-low' : t.status === 'in_progress' ? 'pill-medium' : 'pill-high'}`}>
                  {t.status.replace('_', ' ')}
                </span>
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-[color:var(--border)] p-4 sm:p-5 space-y-4">
                {/* Original message */}
                <div className="panel-soft p-3 sm:p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                    {t.user_name || t.user_email} · {new Date(t.created_at).toLocaleString()}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{t.message}</div>
                </div>

                {/* Conversation */}
                {responses.map((r) => (
                  <div
                    key={r.response_id}
                    className={`panel-soft p-3 sm:p-4 ${r.author_role === 'admin' ? 'border-l-2 border-l-[color:var(--brand)]' : ''}`}
                    data-testid={r.author_role === 'admin' ? 'ticket-admin-reply' : 'ticket-customer-reply'}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest mb-1">
                      <span className={r.author_role === 'admin' ? 'text-[color:var(--brand)]' : 'text-[color:var(--text-secondary)]'}>
                        {r.author_role === 'admin' ? '/ STAFF · ' : ''}{r.author_name || r.author_email}
                      </span>
                      <span className="text-[color:var(--text-muted)]"> · {new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{r.body}</div>
                  </div>
                ))}

                {/* Admin reply box */}
                <div>
                  <div className="label-tag mb-2">// Staff Reply</div>
                  <textarea
                    rows={3}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Reply to this customer…"
                    className="input-base"
                    data-testid="admin-ticket-reply-input"
                  />
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button
                      onClick={() => sendReply(t.ticket_id)}
                      disabled={sending}
                      className="btn-primary !py-2 !text-[11px]"
                      data-testid="admin-ticket-reply-send"
                    >
                      <Send size={12} /> {sending ? 'Sending…' : 'Send Reply'}
                    </button>
                    {t.status !== 'resolved' && (
                      <button
                        onClick={() => resolve(t.ticket_id)}
                        className="btn-secondary !py-2 !text-[11px]"
                        data-testid="admin-ticket-resolve"
                      >
                        <CheckCircle2 size={12} /> Mark Resolved
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const InvoicesView = () => {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get('/admin/customers').then((r) => setCustomers(r.data)); }, []);
  return (
    <div className="font-mono text-xs text-[color:var(--text-secondary)] panel p-6">
      Open any customer (Customers tab) to issue invoices and manage payment status.
      <div className="mt-3">Customers with assigned packages: {customers.filter((c) => c.package && c.package !== 'none').length}</div>
    </div>
  );
};

const LogsTable = ({ logs }) => (
  <div className="panel overflow-x-auto">
    <table className="dtable min-w-[800px]">
      <thead><tr><th>Date</th><th>Action</th><th>Actor</th><th>Detail</th></tr></thead>
      <tbody>
        {logs.length === 0 ? <tr><td colSpan={4} className="text-[color:var(--text-secondary)]">No activity yet.</td></tr> :
        logs.map((l) => (
          <tr key={l.log_id}>
            <td className="text-[color:var(--text-secondary)]">{new Date(l.created_at).toLocaleString()}</td>
            <td><span className="pill pill-brand">{l.action}</span></td>
            <td className="text-[color:var(--text-secondary)]">{l.actor_email || l.user_id || '—'}</td>
            <td>{l.detail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PackageRequests = ({ reqs, reload }) => {
  const update = async (id, status) => { await api.patch(`/admin/package-requests/${id}`, { status }); toast.success(`Marked ${status}`); reload(); };
  return (
    <div className="panel overflow-x-auto">
      <table className="dtable min-w-[800px]">
        <thead><tr><th>Date</th><th>Customer</th><th>Package</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {reqs.length === 0 ? <tr><td colSpan={5} className="text-[color:var(--text-secondary)]">No package requests yet.</td></tr> :
          reqs.map((r) => (
            <tr key={r.request_id}>
              <td className="text-[color:var(--text-secondary)]">{new Date(r.created_at).toLocaleString()}</td>
              <td className="text-[color:var(--brand)]">{r.user_email}</td>
              <td><span className="pill pill-muted">{r.package}</span></td>
              <td><span className={`pill ${r.status === 'approved' ? 'pill-low' : r.status === 'rejected' ? 'pill-critical' : 'pill-medium'}`}>{r.status}</span></td>
              <td className="text-right space-x-1">
                {r.status === 'pending' && (
                  <>
                    <button onClick={() => update(r.request_id, 'approved')} className="btn-secondary !py-1 !text-[10px]">Approve</button>
                    <button onClick={() => update(r.request_id, 'rejected')} className="btn-secondary !py-1 !text-[10px]">Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Empty = ({ children }) => <div className="font-mono text-xs text-[color:var(--text-secondary)] py-4">{children}</div>;

export default AdminDashboard;
